import {
  showOlbAddIncomeStep,
  showOlbEmploymentDetailsStep,
  showOlbLivingArrangementStep,
} from "./olbStepConditions";
import type { FlowStep, NavigationContext } from "../flowConfig";

function isMembershipSelectionStep(context: NavigationContext) {
  return context.applicationState === "membership_selection";
}

function canShowMembershipVerifyEmailStep(context: NavigationContext) {
  return isMembershipSelectionStep(context) || isMembershipSharePendingStep(context);
}

function isMembershipSharePendingStep(context: NavigationContext) {
  return context.applicationState === "membership_share_pending";
}

function canShowMembershipShareStatusStep(context: NavigationContext) {
  return isMembershipSharePendingStep(context) || Boolean(context.accountApplicationUuid);
}

function canShowCoApplicantEmploymentDetailsStep(context: NavigationContext) {
  return (
    (context.isCoApplicantEnabled ?? false) &&
    context.hasCoApplicant === true &&
    (context.showEmploymentDetailsStep ?? true)
  );
}

function canShowRequestedAmountStep(context: NavigationContext) {
  return (
    context.isAutoRequestedAmountEnabled === true &&
    context.hasVehicleSelected === false &&
    context.isRefinance === false
  );
}

export const autoLoanFlow: Record<"web" | "olb", FlowStep[]> = {
  web: [
    // Eligibility selection (only shown if institution has eligibility criteria)
    {
      id: "eligibility",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/auto-loan/${applicationUuid}/eligibility` : "/auto-loan/eligibility",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/auto-loan/eligibility", `/auto-loan/${applicationUuid}/eligibility`]
          : ["/auto-loan/eligibility"],
      condition: (context: NavigationContext) => context.hasEligibilityCriteria === true,
    },

    // MLA covered-borrower question (LNDG-1483 backport; shown when the
    // mla_question additional field is enabled)
    {
      id: "questions",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/auto-loan/${applicationUuid}/questions` : "/auto-loan/questions",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/auto-loan/questions", `/auto-loan/${applicationUuid}/questions`]
          : ["/auto-loan/questions"],
      condition: (context: NavigationContext) => context.hasLendingQuestions === true,
    },

    // Primary applicant information
    {
      id: "phone",
      getPath: ({ applicationUuid }) =>
        applicationUuid ? `/auto-loan/${applicationUuid}/phone` : "/auto-loan/phone",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/auto-loan/phone", `/auto-loan/${applicationUuid}/phone`]
          : ["/auto-loan/phone"],
      condition: (context: NavigationContext) => context.isPlaidLayerEnabled === true,
    },
    {
      id: "personal-info-review",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/auto-loan/${applicationUuid}/personal-info-review`
          : "/auto-loan/personal-info-review",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? [
              "/auto-loan/personal-info-review",
              `/auto-loan/${applicationUuid}/personal-info-review`,
            ]
          : ["/auto-loan/personal-info-review"],
      condition: (context: NavigationContext) =>
        context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill !== true,
    },
    {
      id: "personal-info",
      getPath: ({ applicationUuid }) =>
        applicationUuid
          ? `/auto-loan/${applicationUuid}/personal-info`
          : "/auto-loan/personal-info",
      getMatchPaths: ({ applicationUuid }) =>
        applicationUuid
          ? ["/auto-loan/personal-info", `/auto-loan/${applicationUuid}/personal-info`]
          : ["/auto-loan/personal-info"],
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    {
      id: "address",
      path: "/auto-loan/:applicationUuid/address",
      condition: (context: NavigationContext) =>
        (context.isPlaidLayerEnabled === true && context.skippedPlaidPrefill === true) ||
        context.isPlaidLayerEnabled !== true,
    },
    {
      id: "living-arrangement",
      path: "/auto-loan/:applicationUuid/living-arrangement",
      condition: (context: NavigationContext) => context.showLivingArrangementStep ?? true,
    },
    { id: "add-income", path: "/auto-loan/:applicationUuid/add-income" },
    {
      id: "employment-details",
      path: "/auto-loan/:applicationUuid/employment-details",
      condition: (context: NavigationContext) => context.showEmploymentDetailsStep ?? true,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/auto-loan/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) => context.isCoApplicantEnabled ?? false,
    },
    {
      id: "co-applicant-details",
      path: "/auto-loan/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address-ask",
      path: "/auto-loan/:applicationUuid/co-applicant/address-ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/auto-loan/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) &&
        context.hasCoApplicant === true &&
        context.hasSeparateCoApplicantAddress === true,
    },
    {
      id: "co-applicant-income",
      path: "/auto-loan/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/auto-loan/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },

    // Vehicle selection and details
    { id: "selected-car", path: "/auto-loan/:applicationUuid/selected-car" },
    {
      id: "requested-amount",
      path: "/auto-loan/:applicationUuid/requested-amount",
      condition: canShowRequestedAmountStep,
    },
    {
      id: "auto-details",
      path: "/auto-loan/:applicationUuid/auto-details",
      condition: (context: NavigationContext) => context.hasVehicleSelected === true,
    },

    // Final steps
    { id: "disclosures", path: "/auto-loan/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/auto-loan/:applicationUuid/credit-pull" },
    { id: "approved", path: "/auto-loan/:applicationUuid/approved" },
    {
      id: "membership-share-select",
      path: "/auto-loan/:applicationUuid/membership-share/select",
      condition: isMembershipSelectionStep,
    },
    {
      id: "membership-share-use-different-email",
      path: "/auto-loan/:applicationUuid/membership-share/use-different-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-verify-email",
      path: "/auto-loan/:applicationUuid/membership-share/verify-email",
      condition: canShowMembershipVerifyEmailStep,
    },
    {
      id: "membership-share-activate",
      path: "/auto-loan/:applicationUuid/membership-share/activate",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-disclosures",
      path: "/auto-loan/:applicationUuid/membership-share/disclosures",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-processing",
      path: "/auto-loan/:applicationUuid/membership-share/processing",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-documents-requested",
      path: "/auto-loan/:applicationUuid/membership-share/documents-requested",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-submitting-documents",
      path: "/auto-loan/:applicationUuid/membership-share/submitting-documents",
      condition: isMembershipSharePendingStep,
    },
    {
      id: "membership-share-status",
      path: "/auto-loan/:applicationUuid/membership-share/status",
      condition: canShowMembershipShareStatusStep,
    },
    { id: "submitted", path: "/auto-loan/:applicationUuid/submitted" },
  ],
  olb: [
    // Application start
    { id: "confirm-info", path: "/olb-auto-loan/:applicationUuid/confirm-info" },
    {
      id: "living-arrangement",
      path: "/olb-auto-loan/:applicationUuid/living-arrangement",
      condition: showOlbLivingArrangementStep,
    },
    {
      id: "add-income",
      path: "/olb-auto-loan/:applicationUuid/add-income",
      condition: showOlbAddIncomeStep,
    },
    {
      id: "employment-details",
      path: "/olb-auto-loan/:applicationUuid/employment-details",
      condition: showOlbEmploymentDetailsStep,
    },

    // Co-applicant flow (conditional)
    {
      id: "co-applicant-ask",
      path: "/olb-auto-loan/:applicationUuid/co-applicant/ask",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant !== false,
    },
    {
      id: "co-applicant-details",
      path: "/olb-auto-loan/:applicationUuid/co-applicant/details",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-address",
      path: "/olb-auto-loan/:applicationUuid/co-applicant/address",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-income",
      path: "/olb-auto-loan/:applicationUuid/co-applicant/income",
      condition: (context: NavigationContext) =>
        (context.isCoApplicantEnabled ?? false) && context.hasCoApplicant === true,
    },
    {
      id: "co-applicant-employment-details",
      path: "/olb-auto-loan/:applicationUuid/co-applicant/employment-details",
      condition: canShowCoApplicantEmploymentDetailsStep,
    },

    // Vehicle selection and details
    { id: "selected-car", path: "/olb-auto-loan/:applicationUuid/selected-car" },
    {
      id: "requested-amount",
      path: "/olb-auto-loan/:applicationUuid/requested-amount",
      condition: canShowRequestedAmountStep,
    },
    {
      id: "auto-details",
      path: "/olb-auto-loan/:applicationUuid/auto-details",
      condition: (context: NavigationContext) => context.hasVehicleSelected === true,
    },

    // Final steps
    { id: "disclosures", path: "/olb-auto-loan/:applicationUuid/disclosures" },
    { id: "credit-pull", path: "/olb-auto-loan/:applicationUuid/credit-pull" },
    { id: "approved", path: "/olb-auto-loan/:applicationUuid/approved" },
    { id: "submitted", path: "/olb-auto-loan/:applicationUuid/submitted" },
  ],
};
